import React, { useEffect, useMemo, useRef, useState, } from 'react';
import { ButtonContainer, ImageButtonContainer, LinkList, Root, TeaserContainer, TeaserWrapper } from './styles';
import { Button, BUTTON_TYPES } from '../Button/Button';
import datetime from '../primitives/Datetime';
import { useInView } from 'react-intersection-observer';
import Expired from './Expired/Expired';
import { Placeholder } from '../primitives/Placeholder';
import TeaserContextMenu from './TeaserContextMenu/TeaserContextMenu';
import TeaserImage from './TeaserImage/TeaserImage';
import TeaserText from './TeaserText/TeaserText';
import { useOnClick } from '../primitives/teaser/teaserHooks';
import TeaserButton from '../primitives/teaser/TeaserButton';
import hasHover from '../primitives/hasHover';
import Hidden from '../primitives/Hidden';
import { cx } from '@linaria/core';
import Icon from '../Icon/Icon';
function useTitle(isLoading, textAlignment = 'left', teaserContainerRef, longTitle, shortTitle, mediumTitle, title) {
    const width = useWindowWidth(teaserContainerRef);
    if (isLoading) {
        return (React.createElement("span", { className: cx(Placeholder.base, Placeholder.alignment[textAlignment]), style: { width: '60%' } },
            React.createElement("span", { className: cx(Hidden.base) }, "L\u00E4dt")));
    }
    else if (width > 1280 && longTitle) {
        return longTitle;
    }
    else if (width <= 480 && shortTitle) {
        return shortTitle;
    }
    else if (mediumTitle) {
        return mediumTitle;
    }
    else {
        return title ?? null;
    }
}
function useWindowWidth(teaserContainerRef, initialWidth = 1024) {
    const [width, setWidth] = useState(initialWidth);
    useEffect(() => {
        function onResize() {
            setWidth(window.innerWidth);
        }
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [teaserContainerRef]);
    return width;
}
function useTeaser({ textPosition, isExpired, imageRef, image, ratio, isLoadingEagerly, isLoading, maturityRating, hasSubtitles, duration, type, progress, episode, showText, textAlignment, videoLinkText, programDate, programTime, teaserTitle, subline, availableTo, isShowingAvailableTime, overline, text, cornerLogo, hasHiddenTitle, imageChildren, }) {
    return useMemo(() => {
        return (React.createElement("div", { className: cx(TeaserWrapper.base, isExpired && TeaserWrapper.expired) },
            React.createElement(TeaserImage, { textPosition: textPosition, image: image, imageRef: imageRef, isLoading: isLoading, isLoadingEagerly: isLoadingEagerly, type: type, imageRatio: ratio, programTime: programTime, progress: progress, duration: duration, availableTo: availableTo, cornerLogo: cornerLogo, episode: episode, title: teaserTitle, subline: subline, textAlignment: textAlignment, hasHiddenTitle: hasHiddenTitle }, imageChildren),
            showText && (React.createElement(TeaserText, { type: type, textPosition: textPosition, textAlignment: textAlignment, hasSubtitles: hasSubtitles, title: teaserTitle, isLoading: isLoading, maturityRating: maturityRating, isShowingAvailableTime: isShowingAvailableTime, availableTo: availableTo, programTime: programTime, programDate: programDate, text: text, subline: subline, overline: overline }))));
    }, [
        availableTo,
        isShowingAvailableTime,
        duration,
        image,
        isLoadingEagerly,
        isLoading,
        maturityRating,
        overline,
        programDate,
        programTime,
        progress,
        episode,
        ratio,
        subline,
        text,
        textAlignment,
        textPosition,
        type,
        videoLinkText,
        cornerLogo,
        isExpired,
        showText,
        hasSubtitles,
        imageChildren,
        teaserTitle,
    ]);
}
function onMouseEnter(teaserContainerRef, heightRef, timeoutRef) {
    if (matchMedia('(hover: hover)').matches) {
        if (teaserContainerRef && heightRef) {
            const teaserContainer = teaserContainerRef.current;
            const height = heightRef.current;
            if (teaserContainer && height) {
                const teaserRect = teaserContainer.getBoundingClientRect();
                let positionHorizontal = 'default';
                let positionVertical = 'default';
                if (teaserRect.left <= 100) {
                    positionHorizontal = 'left';
                }
                else if (teaserRect.right >= window.innerWidth - 100) {
                    positionHorizontal = 'right';
                }
                if (teaserRect.bottom >= window.innerHeight - 150) {
                    positionVertical = 'bottom';
                }
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
                if (!teaserContainer.classList.contains('animating')) {
                    height.style.height = `${teaserContainer.offsetHeight}px`;
                    teaserContainer.style.position = 'absolute';
                    teaserContainer.classList.add(positionHorizontal, positionVertical);
                    teaserContainer.classList.add('hover', 'animating');
                }
            }
        }
    }
}
function onMouseLeave(teaserContainerRef, heightRef, timeoutRef) {
    if (matchMedia('(hover: hover)').matches) {
        if (teaserContainerRef && heightRef) {
            const teaserContainer = teaserContainerRef.current;
            const height = heightRef.current;
            if (teaserContainer && height) {
                teaserContainer.classList.remove('hover');
                timeoutRef.current = setTimeout(() => {
                    height.style.height = `0`;
                    teaserContainer.style.position = 'relative';
                    teaserContainer.classList.remove('animating', 'default', 'left', 'right', 'bottom');
                }, 300);
            }
        }
    }
}
export function Teaser({ ratio = '16x9', onClick, onVisible, type = 'media', textPosition = 'belowImage', textAlignment = 'left', hasHiddenTitle = false, label, progress, episode, image = {}, duration, maturityRating, hasSubtitles, programDate, programTime, title, longTitle, mediumTitle, shortTitle, subline, availableTo, isShowingAvailableTime = false, overline, text, links = [], videoLink, isLoadingEagerly = false, hasDisabledAnimations = false, href, target, as: LinkComponent, hoverEffect = 'default', hasClickEffect, clickEffectOffsetY, index, id, isExpired = false, isLoading = false, isInWatchlist = false, cornerLogo, tooltip, teaserButtons = [], }) {
    /** Variables */
    subline =
        subline && isLoading ? (React.createElement("span", { className: cx(Placeholder.base, Placeholder.alignment[textAlignment]), style: { width: '40%' } })) : (subline);
    text = text && isLoading ? React.createElement("span", { className: cx(Placeholder.base) }) : text;
    overline = overline && isLoading ? React.createElement("span", { className: cx(Placeholder.base), style: { width: '25%' } }) : overline;
    const imageRef = useRef(null);
    const onClickMemo = useOnClick(imageRef, hasDisabledAnimations, onClick, hasClickEffect, clickEffectOffsetY);
    const [ref, inView] = useInView({ threshold: 0.5, triggerOnce: true });
    const teaserContainerRef = useRef(null);
    const heightRef = useRef(null);
    const timeoutRef = useRef(null);
    const teaserTitle = useTitle(isLoading, textAlignment, teaserContainerRef, longTitle, shortTitle, mediumTitle, title);
    const showText = !!(programDate ||
        programTime ||
        label ||
        (hasHiddenTitle ? false : teaserTitle) ||
        subline ||
        (availableTo && datetime.getFormatRemainingTime(0, availableTo)) ||
        overline ||
        text);
    const hasButtons = teaserButtons && teaserButtons.length > 0;
    const imageChildren = textPosition === 'onImage' && hasHover() && hasButtons ? (React.createElement("div", { className: cx(ImageButtonContainer.base) }, teaserButtons.map((button, i) => (React.createElement(TeaserButton, { buttonProps: button, key: i, teaserIndex: index }))))) : null;
    const teaser = useTeaser({
        textPosition,
        isExpired,
        imageRef,
        image,
        ratio,
        isLoadingEagerly,
        isLoading,
        maturityRating,
        hasSubtitles,
        duration,
        type,
        progress,
        episode,
        showText,
        textAlignment,
        videoLinkText: videoLink?.text,
        programDate,
        programTime,
        teaserTitle,
        subline,
        availableTo,
        isShowingAvailableTime,
        overline,
        text,
        cornerLogo,
        hasHiddenTitle,
        imageChildren,
    });
    /** Construktor-Hook & Effect-Hooks */
    useEffect(() => {
        if (inView && onVisible && id) {
            onVisible(index ?? null);
        }
    }, [inView, id]);
    /** JSX */
    return (React.createElement("div", { className: cx(Root.base, Root.textPosition[textPosition], Root.textAlignment[textAlignment], videoLink?.text && textPosition === 'onImage' && Root.textPosition.onImageWithVideoLink, videoLink?.text && textAlignment === 'center' && Root.textAlignment.centerWithVideoLink, videoLink?.text && textAlignment === 'right' && Root.textAlignment.rightWithVideoLink, showText && Root.showText, isLoading && Root.isLoading), title: tooltip, ref: ref, onMouseEnter: () => onMouseEnter(teaserContainerRef, heightRef, timeoutRef), onMouseLeave: () => onMouseLeave(teaserContainerRef, heightRef, timeoutRef) },
        React.createElement("div", { ref: heightRef }),
        React.createElement("div", { className: cx(TeaserContainer.base, TeaserContainer.hoverEffect[hoverEffect], hoverEffect === 'scale' && !!episode && TeaserContainer.bottomAreaTransparentOnHover), ref: teaserContainerRef },
            isExpired && React.createElement(Expired, null),
            LinkComponent ? (React.createElement(LinkComponent, { to: href, onClick: onClickMemo }, teaser)) : href ? (React.createElement("a", { href: href, onClick: onClickMemo, target: target, rel: target ? 'noopener' : undefined }, teaser)) : (teaser),
            textPosition !== 'onImage' && hasHover() && hasButtons && (React.createElement("div", { className: cx(ButtonContainer.base) }, teaserButtons.map((button, i) => (React.createElement(TeaserButton, { buttonProps: button, key: i, teaserIndex: index }))))),
            links.length > 0 && (React.createElement("div", { className: cx(LinkList.base) },
                React.createElement("ul", null, links.map((link) => (React.createElement("li", { key: link.text + link.href },
                    React.createElement(Button, { text: link.text, href: link.href }))))))),
            videoLink?.text && videoLink.href && (React.createElement(Button, { href: videoLink.href, type: BUTTON_TYPES.PRIMARY, text: videoLink.text, iconLeft: React.createElement(Icon, { name: "playL", type: "solid" }) })),
            !hasHover() && hasButtons && React.createElement(TeaserContextMenu, { items: teaserButtons, isInWatchlist: isInWatchlist }))));
}
export default React.memo(Teaser);
